import { useMutation, useQuery } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { IonCol, IonGrid, IonRow } from '@ionic/react';
import { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import styles from '@components/automations/forms/AutomationTriggerBlockForm.module.css';
import ActionIconButton from '@components/buttons/ActionIconButton';
import TertiaryButton from '@components/buttons/TertiaryButton';
import ArticleSelect from '@components/fields/ArticleSelect';
import BaseErrors from '@components/fields/BaseErrors';
import CheckboxGroup from '@components/fields/CheckboxGroup';
import FormButtons from '@components/fields/FormButtons';
import GoalSelect from '@components/fields/GoalSelect';
import Input from '@components/fields/Input';
import RequiredFieldIndicator from '@components/fields/RequiredFieldIndicator';
import Select from '@components/fields/Select';
import TagSelect from '@components/fields/TagSelect';
import Form from '@components/forms/Form';
import FormCondition from '@components/forms/FormCondition';
import InputLabel from '@components/typography/InputLabel';
import InputSubLabel from '@components/typography/InputSubLabel';
import {
  AutomationTriggerBlockFrequencyEnum,
  AutomationTriggerBlockKindEnum,
  AutomationTriggerBlockRequiredFieldEnum,
  AutomationTriggerBlockUpdateDocument,
  AutomationTriggerBlockUpdateMutation,
  AutomationTriggerBlockUpdateMutationVariables,
  AutomationFragment as AutomationType,
  AutomationUserTypeEnum,
  CareProfileActionableFieldEnum,
  CareProfileActionableFieldValueEnum,
  DurationUnitsEnum,
  GetAutomationTriggerBlockRulesDocument,
  GetAutomationTriggerBlockRulesQuery,
  GetAutomationTriggerBlockRulesQueryVariables,
  SignedDocumentKindEnum,
  TagTypeEnum,
  TaskClientVisibleEnum,
  TaskKindEnum,
  TrackerTypeEnum
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import {
  optionsForAutomationTriggerBlockFrequency,
  optionsForAutomationTriggerBlockKinds,
  optionsForAutomationUserType,
  optionsForCareProfileActionableField,
  optionsForCareProfileActionableFieldValue,
  optionsForDurationUnits,
  optionsForSignedDocumentKind,
  optionsForTaskClientVisible,
  optionsForTaskKinds,
  optionsForTrackerType
} from '@utils/optionUtils';

type AutomationTriggerBlock = Extract<AutomationType['blocks'][number], { __typename: 'AutomationTriggerBlock' }>;

type FormData = {
  articleId: string | null;
  careProfileField: CareProfileActionableFieldEnum | null;
  careProfileValues: CareProfileActionableFieldValueEnum[] | null;
  duration: number | null;
  durationUnits: DurationUnitsEnum | null;
  freeTextKeywords: { keyword: string | null }[];
  frequency: AutomationTriggerBlockFrequencyEnum | null;
  goalTemplateId: string | null;
  kind: AutomationTriggerBlockKindEnum;
  signedDocumentKind: SignedDocumentKindEnum | null;
  tag: string | null;
  taskClientVisible: TaskClientVisibleEnum | null;
  taskKind: TaskKindEnum | null;
  trackerType: TrackerTypeEnum | null;
  userType: AutomationUserTypeEnum;
};

type Props = {
  automationTriggerBlock: AutomationTriggerBlock;
  locked: boolean;
};

const AutomationTriggerBlockForm = ({ automationTriggerBlock, locked }: Props) => {
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [durationUnits, setDurationUnits] = useState<DurationUnitsEnum[]>([]);
  const [requiredFields, setRequiredFields] = useState<AutomationTriggerBlockRequiredFieldEnum[]>([]);
  const [availableUserTypes, setAvailableUserTypes] = useState<AutomationUserTypeEnum[]>([]);
  const [availableFrequencies, setAvailableFrequencies] = useState<AutomationTriggerBlockFrequencyEnum[]>([]);

  const getAutomationTriggerBlockRulesQuery = useQuery<
    GetAutomationTriggerBlockRulesQuery,
    GetAutomationTriggerBlockRulesQueryVariables
  >(GetAutomationTriggerBlockRulesDocument);

  const initialValues: FormData = {
    articleId: automationTriggerBlock.articleId ?? '',
    careProfileField: automationTriggerBlock.careProfileField,
    careProfileValues: automationTriggerBlock.careProfileValues,
    duration: automationTriggerBlock.triggerDuration,
    durationUnits: automationTriggerBlock.triggerDurationUnits,
    freeTextKeywords: automationTriggerBlock.freeTextKeywords.map(keyword => ({
      keyword
    })),
    frequency: automationTriggerBlock.frequency,
    goalTemplateId: automationTriggerBlock.goalTemplateId,
    kind: automationTriggerBlock.triggerBlockKind,
    signedDocumentKind: automationTriggerBlock.signedDocumentKind,
    tag: automationTriggerBlock.tag,
    taskClientVisible: automationTriggerBlock.triggerTaskClientVisible,
    taskKind: automationTriggerBlock.taskKind,
    trackerType: automationTriggerBlock.trackerType,
    userType: automationTriggerBlock.triggerBlockUserType ?? ''
  };

  // This lets the fields that are conditionally required be validated on the server, since there's
  // not much point in reproducing that complex logic here, and also I couldn't get it to work when
  // changing the type of trigger.
  const schema = yup
    .object()
    .shape({
      articleId: yup.string().nullable(),
      frequency: yup
        .string()
        .oneOf(
          Object.values(AutomationTriggerBlockFrequencyEnum),
          intl.formatMessage({ id: 'errors.automationTriggerBlock.frequency.inclusion' })
        ),
      kind: yup
        .string()
        .required(intl.formatMessage({ id: 'errors.automationTriggerBlock.kind.blank' }))
        .oneOf(
          Object.values(AutomationTriggerBlockKindEnum),
          intl.formatMessage({ id: 'errors.automationTriggerBlock.kind.inclusion' })
        ),
      userType: yup
        .string()
        .required(intl.formatMessage({ id: 'errors.automationTriggerBlock.userType.blank' }))
        .oneOf(
          Object.values(AutomationUserTypeEnum),
          intl.formatMessage({ id: 'errors.automationTriggerBlock.userType.inclusion' })
        )
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const {
    append: freeTextKeywordsAppend,
    fields: freeTextKeywordFields,
    remove: freeTextKeywordsRemove
  } = useFieldArray<FormData>({
    control: form.control,
    name: 'freeTextKeywords'
  });

  const careProfileField = form.watch('careProfileField');
  const kind = form.watch('kind');

  useEffect(() => {
    form.setValue('careProfileValues', []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careProfileField]);

  useEffect(() => {
    const rule = getAutomationTriggerBlockRulesQuery.data?.automationTriggerBlockRules.find(rule => rule.kind === kind);
    if (rule) {
      setDurationUnits(rule.durationUnits ?? []);
      setAvailableFrequencies(rule.frequencies);
      setAvailableUserTypes(rule.userTypes);
      setRequiredFields(rule.requiredFields);
    } else {
      setAvailableFrequencies([]);
      setAvailableUserTypes([]);
      setRequiredFields([]);
    }
  }, [getAutomationTriggerBlockRulesQuery.data?.automationTriggerBlockRules, kind]);

  const [updateAutomationTriggerBlock] = useMutation<
    AutomationTriggerBlockUpdateMutation,
    AutomationTriggerBlockUpdateMutationVariables
  >(AutomationTriggerBlockUpdateDocument);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    const values = form.getValues();
    if ((values.duration as number | string | null) === '') {
      values.duration = null;
    }
    const freeTextKeywords = values.freeTextKeywords.filter(kw => !!kw).map(kw => kw.keyword as string);
    return updateAutomationTriggerBlock({
      variables: {
        ...values,
        freeTextKeywords,
        id: automationTriggerBlock.id
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.automationTriggerBlockUpdate,
        intl,
        onError: () => {},
        onSuccess: () => {},
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.automationTriggerBlock'
      });
    });
  }, [updateAutomationTriggerBlock, form, automationTriggerBlock.id, intl]);

  const anyOption = { label: intl.formatMessage({ id: 'dictionary.any' }), value: '' };

  const careProfileFieldOptions = optionsForCareProfileActionableField(intl);
  const careProfileValueOptions = optionsForCareProfileActionableFieldValue(careProfileField, intl);
  const durationUnitsOptions = optionsForDurationUnits(intl, durationUnits);
  const frequencyOptions = optionsForAutomationTriggerBlockFrequency(intl, availableFrequencies);
  const kindOptions = optionsForAutomationTriggerBlockKinds(intl);
  const signedDocumentKindOptions = optionsForSignedDocumentKind(intl);
  const taskClientVisibleOptions = optionsForTaskClientVisible(intl);
  const taskKindOptions = [anyOption].concat(optionsForTaskKinds(intl));
  const trackerTypeOptions = optionsForTrackerType(intl);
  const userTypeOptions = optionsForAutomationUserType(intl, availableUserTypes);

  return (
    <Form data-test="automation-trigger-block-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Select<FormData>
        control={form.control}
        disabled={locked}
        error={form.formState.errors.kind}
        label={intl.formatMessage({ id: 'forms.automationTriggerBlock.kindLabel' })}
        name="kind"
        options={kindOptions}
        required
      />
      <Select<FormData>
        control={form.control}
        disabled={locked}
        error={form.formState.errors.userType}
        label={intl.formatMessage({ id: 'forms.automationTriggerBlock.userTypeLabel' })}
        name="userType"
        options={userTypeOptions}
        required
      />
      <Select<FormData>
        control={form.control}
        disabled={locked}
        error={form.formState.errors.frequency}
        label={intl.formatMessage({ id: 'forms.automationTriggerBlock.frequencyLabel' })}
        name="frequency"
        options={frequencyOptions}
        required
      />
      <FormCondition
        condition={requiredFields.includes(AutomationTriggerBlockRequiredFieldEnum.TAG)}
        fieldName="tag"
        form={form}
      >
        <TagSelect<FormData>
          allowAny
          control={form.control}
          disabled={locked}
          errors={form.formState.errors.tag}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.tagLabel' })}
          name="tag"
          required
          single
          tagType={TagTypeEnum.JOURNEY}
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationTriggerBlockRequiredFieldEnum.DURATION)}
        fieldName="duration"
        form={form}
      >
        <Input<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.duration}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.durationLabel' })}
          name="duration"
          required
          type="number"
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationTriggerBlockRequiredFieldEnum.DURATION_UNITS)}
        fieldName="durationUnits"
        form={form}
      >
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.durationUnits}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.durationUnitsLabel' })}
          name="durationUnits"
          options={durationUnitsOptions}
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationTriggerBlockRequiredFieldEnum.SIGNED_DOCUMENT_KIND)}
        fieldName="signedDocumentKind"
        form={form}
      >
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.signedDocumentKind}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.signedDocumentKindLabel' })}
          name="signedDocumentKind"
          options={signedDocumentKindOptions}
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationTriggerBlockRequiredFieldEnum.TRACKER_TYPE)}
        fieldName="trackerType"
        form={form}
      >
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.trackerType}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.trackerTypeLabel' })}
          name="trackerType"
          options={trackerTypeOptions}
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationTriggerBlockRequiredFieldEnum.ARTICLE_ID)}
        fieldName="articleId"
        form={form}
      >
        <ArticleSelect<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.articleId}
          initialOption={{ label: automationTriggerBlock.article?.title, value: automationTriggerBlock.articleId }}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.articleIdLabel' })}
          name="articleId"
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationTriggerBlockRequiredFieldEnum.GOAL_TEMPLATE_ID)}
        fieldName="goalTemplateId"
        form={form}
      >
        <GoalSelect<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.goalTemplateId}
          initialOption={{
            label: automationTriggerBlock.goalTemplate?.title,
            value: automationTriggerBlock.goalTemplateId
          }}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.goalIdLabel' })}
          name="goalTemplateId"
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationTriggerBlockRequiredFieldEnum.CARE_PROFILE_FIELD)}
        fieldName="careProfileField"
        form={form}
      >
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.careProfileField}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.careProfileFieldLabel' })}
          name="careProfileField"
          options={careProfileFieldOptions}
          required
        />
      </FormCondition>
      <FormCondition
        condition={
          requiredFields.includes(AutomationTriggerBlockRequiredFieldEnum.CARE_PROFILE_VALUES) &&
          careProfileValueOptions.length > 0
        }
        fieldName="careProfileValues"
        form={form}
      >
        <CheckboxGroup<FormData>
          control={form.control}
          disabled={locked}
          errors={form.formState.errors.careProfileValues}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.careProfileValuesLabel' })}
          name="careProfileValues"
          options={careProfileValueOptions ?? []}
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationTriggerBlockRequiredFieldEnum.TASK_CLIENT_VISIBLE)}
        fieldName="taskClientVisible"
        form={form}
      >
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.taskClientVisible}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.taskClientVisibleLabel' })}
          name="taskClientVisible"
          options={taskClientVisibleOptions}
          required
        />
      </FormCondition>
      <FormCondition
        condition={requiredFields.includes(AutomationTriggerBlockRequiredFieldEnum.TASK_KIND)}
        fieldName="taskKind"
        form={form}
      >
        <Select<FormData>
          control={form.control}
          disabled={locked}
          error={form.formState.errors.taskKind}
          label={intl.formatMessage({ id: 'forms.automationTriggerBlock.taskKindLabel' })}
          name="taskKind"
          options={taskKindOptions}
          required
        />
      </FormCondition>
      <div
        className={
          requiredFields.includes(AutomationTriggerBlockRequiredFieldEnum.FREE_TEXT_KEYWORDS) ? '' : 'ion-hide'
        }
      >
        <div className={styles.freeTextKeywords}>
          <div className={styles.labelWrapper}>
            <InputLabel>
              <FormattedMessage id="forms.automationTriggerBlock.freeTextKeywordsLabel" />
              <RequiredFieldIndicator />
            </InputLabel>
            <div>
              <InputSubLabel>
                <FormattedMessage id="forms.automationTriggerBlock.freeTextExplanation1" />
                <ul>
                  <li>
                    <FormattedMessage id="forms.automationTriggerBlock.freeTextType1" />
                  </li>
                  <li>
                    <FormattedMessage id="forms.automationTriggerBlock.freeTextType2" />
                  </li>
                  <li>
                    <FormattedMessage id="forms.automationTriggerBlock.freeTextType3" />
                  </li>
                </ul>
              </InputSubLabel>
            </div>
          </div>
          {freeTextKeywordFields.map((field, index) => (
            <IonGrid className={'ion-no-margin ion-no-padding '} key={field.id}>
              <IonRow className="ion-justify-content-center ion-align-items-center">
                <IonCol>
                  <div>
                    <Input<FormData>
                      control={form.control}
                      data-test="email-input"
                      disabled={locked}
                      error={form.formState.errors.freeTextKeywords?.[index]?.keyword}
                      name={`freeTextKeywords.${index}.keyword`}
                    />
                  </div>
                </IonCol>
                <IonCol className={styles.keywordRemove} size="auto">
                  <div className="ion-margin-start">
                    <ActionIconButton
                      ariaLabelKey="accessibility.ariaLabels.delete"
                      disabled={locked}
                      icon="times"
                      onClick={() => freeTextKeywordsRemove(index)}
                    />
                  </div>
                </IonCol>
              </IonRow>
            </IonGrid>
          ))}

          <TertiaryButton
            disabled={locked}
            icon="add"
            onClick={() => freeTextKeywordsAppend({ keyword: '' })}
            textKey={
              freeTextKeywordFields.length > 0
                ? 'forms.automationTriggerBlock.addAnotherKeyword'
                : 'forms.automationTriggerBlock.addAKeyword'
            }
          />
        </div>
      </div>

      {!locked && <FormButtons hookForm={form} saveDisabled={!form.formState.isDirty} skipCancel />}
    </Form>
  );
};

export default AutomationTriggerBlockForm;
